import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import axios from '../../utils/axiosConfig'

// Heroicons (outline)
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

import { ArrowPathIcon } from '@heroicons/react/24/solid'

const EmailChangeConfirmation = () => {
  const { t } = useTranslation()
  const [status, setStatus] = useState('loading')
  const [newEmail, setNewEmail] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const role = searchParams.get('role')

  useEffect(() => {
    const confirmEmailChange = async () => {
      try {
        const { data } = await axios.post('/auth/confirm-email-change', {
          token,
          role,
        })
        setNewEmail(data?.email || null)
        setStatus('success')
      } catch (err) {
        console.error('Email change confirmation failed:', err)
        // 409: the address got taken between request and confirmation
        if (err?.response?.status === 409) {
          setErrorMessage(t('email_change.confirm_taken'))
        }
        setStatus('error')
      }
    }

    if (token && role) {
      confirmEmailChange()
    } else {
      setStatus('invalid')
    }
  }, [token, role, t])

  const handleRedirect = () => {
    navigate('/')
  }

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return {
          icon: (
            <ArrowPathIcon className="w-10 h-10 text-blue-500 animate-spin" />
          ),
          message: t('email_change.confirm_loading'),
        }
      case 'success':
        return {
          icon: <CheckCircleIcon className="w-10 h-10 text-green-500" />,
          message: newEmail
            ? t('email_change.confirm_success_with_email', { email: newEmail })
            : t('email_change.confirm_success'),
        }
      case 'error':
        return {
          icon: <XCircleIcon className="w-10 h-10 text-red-500" />,
          message: errorMessage || t('email_change.confirm_error'),
        }
      case 'invalid':
        return {
          icon: (
            <ExclamationTriangleIcon className="w-10 h-10 text-yellow-500" />
          ),
          message: t('email_change.confirm_invalid'),
        }
      default:
        return null
    }
  }

  const { icon, message } = renderContent()

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
        <div className="flex justify-center mb-4">{icon}</div>
        <p className="text-lg font-medium text-gray-700 mb-6">{message}</p>
        <button
          onClick={handleRedirect}
          disabled={status === 'loading'}
          className={`inline-flex items-center px-4 py-2 rounded-xl transition ${
            status === 'loading'
              ? 'bg-yellow-05/20 text-white cursor-not-allowed'
              : 'bg-yellow-05/80 text-white font- hover:bg-yellow-05'
          }`}
        >
          {t('email_change.go_home')}
        </button>
      </div>
    </div>
  )
}

export default EmailChangeConfirmation
